import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export type GoalType = 'goal' | 'okr';
export type GoalStatus = 'active' | 'completed' | 'archived';
export type GoalProgressMode = 'manual' | 'linked_tasks' | 'linked_projects';

export interface PmGoal {
  id: string;
  organization_id: string;
  owner_id: string;
  type: GoalType;
  title: string;
  description?: string | null;
  status: GoalStatus;
  start_date?: string | null;
  due_date?: string | null;
  progress_mode: GoalProgressMode;
  target_value: number;
  current_value: number;
  unit?: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface PmGoalLink {
  id: string;
  organization_id: string;
  goal_id: string;
  entity_type: 'project' | 'task';
  entity_id: string;
  weight: number;
  created_at: string;
  updated_at: string;
}

export interface GoalProgressResponse {
  goal: PmGoal;
  links: PmGoalLink[];
  progress_percent: number;
  linked: { projects: number; tasks: number };
  tasks?: { total: number; done: number };
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

async function parseJsonSafe(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

const API_BASE_URL = `${ENV.API_URL}/api/project-management/goals`;

export const goalsService = {
  async list(params: { organization_id: string; status?: string; type?: string; owner_id?: string }): Promise<{ data: PmGoal[]; error: any }> {
    try {
      const token = await getToken();
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });

      const res = await fetch(`${API_BASE_URL}?${qs.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: [], error: json?.error || 'Failed to fetch goals' };
      return { data: (json?.data || []) as PmGoal[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async create(params: {
    organization_id: string;
    type?: GoalType;
    title: string;
    description?: string | null;
    status?: GoalStatus;
    start_date?: string | null;
    due_date?: string | null;
    progress_mode?: GoalProgressMode;
    target_value?: number;
    current_value?: number;
    unit?: string | null;
    metadata?: Record<string, any>;
  }): Promise<{ data: PmGoal | null; error: any }> {
    try {
      const token = await getToken();
      const res = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to create goal' };
      return { data: (json?.data || null) as PmGoal | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async update(goalId: string, organizationId: string, updates: Partial<PmGoal>): Promise<{ data: PmGoal | null; error: any }> {
    try {
      const token = await getToken();
      const qs = new URLSearchParams({ organization_id: organizationId });
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(goalId)}?${qs.toString()}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...updates, organization_id: organizationId }),
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to update goal' };
      return { data: (json?.data || null) as PmGoal | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async remove(goalId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const qs = new URLSearchParams({ organization_id: organizationId });
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(goalId)}?${qs.toString()}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { error: json?.error || 'Failed to delete goal' };
      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },

  async getProgress(goalId: string, organizationId: string): Promise<{ data: GoalProgressResponse | null; error: any }> {
    try {
      const token = await getToken();
      const qs = new URLSearchParams({ organization_id: organizationId });
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(goalId)}/progress?${qs.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to fetch goal progress' };
      return { data: (json?.data || null) as GoalProgressResponse | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async addLink(goalId: string, params: { organization_id: string; entity_type: 'project' | 'task'; entity_id: string; weight?: number }): Promise<{ data: any; error: any }> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(goalId)}/links`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to add link' };
      return { data: json?.data || null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async removeLink(goalId: string, params: { organization_id: string; entity_type: 'project' | 'task'; entity_id: string }): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const qs = new URLSearchParams({
        organization_id: params.organization_id,
        entity_type: params.entity_type,
        entity_id: params.entity_id,
      });
      const res = await fetch(`${API_BASE_URL}/${encodeURIComponent(goalId)}/links?${qs.toString()}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { error: json?.error || 'Failed to remove link' };
      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },
};
